import React from "react";
import { Card } from "../core/Card";
import { Badge } from "../core/Badge";
import { Button } from "../core/Button";
import { Avatar } from "../core/Avatar";
import { Icon } from "../core/Icon";

/**
 * Public FixyBee card. Used by the directory, AI search results and Dany's
 * inline results — one component, so the card a visitor sees in a conversation
 * is the card they see in the directory.
 *
 * **The trust signals are the point of the card, so they are at the top.**
 * FixyDany has three of them — rating, jobs completed and identity
 * verification — and until DY1.6-UX the first two were grey mono text in the
 * footer and the third an unlabelled 15px icon beside the name. The card is scanned in about a second, and what
 * that second landed on was the name and the distance: the two things that do
 * not answer "can I trust this person with my kitchen".
 *
 * So they are a row of chips directly under the name, and location and distance
 * drop to a quieter line beneath them. Same information, opposite hierarchy.
 *
 * **Nothing negative is ever rendered.** No reviews yet shows as "New on
 * FixyDany" rather than "0 ★", which is the same fact without the verdict — a
 * new FixyBee has not been rated badly, they have not been rated. No
 * verification simply omits the chip.
 *
 * **The plan is never on the card** (PLANES M5, 2026-09-30). There used to be
 * a FixyMaster / FixyPro chip and a featured frame for Master. A plan is what
 * a FixyBee pays, not something a client can trust — the argument this card
 * already made for never showing "FixyStarter", now made for all three.
 *
 * Labels are props with English defaults, because `ds/` is translation-free by
 * convention — the i18n-aware caller passes the strings.
 */
export function ProviderCard({
  name, company, city, state, avatarUrl, verified = false,
  rating, jobsCount = 0, bio, distance, ctaLabel = "Visit page", onVisit,
  verifiedLabel = "Verified", newLabel = "New on FixyDany", jobsLabel,
}) {
  const ratingValue = Number(rating) || 0;
  const jobs = Number(jobsCount) || 0;
  // Pre-formatted by the caller so plurals belong to the translator, not here.
  const jobsText = jobsLabel || `${jobs} ${jobs === 1 ? "job" : "jobs"}`;

  return (
    <Card variant="default" interactive padding="md">
      <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
        <Avatar src={avatarUrl} name={company || name} size="lg" />
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* `anywhere` rather than a clamp: a company name is the one string
              on this card a reader must be able to read in full, and at 360px
              an unbroken "Hernandez-Villalobos" would otherwise push the card
              wider than the screen. */}
          <p style={{
            fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
            fontSize: "var(--text-base)", color: "var(--text-body)",
            overflowWrap: "anywhere",
          }}>{company || name}</p>

          {/* The four signals. `nowrap` on each chip so the row breaks between
              chips rather than inside one — at 360px this wraps to two tidy
              lines instead of splitting "FixyMaster" across them. */}
          <div style={{
            display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)",
            flexWrap: "wrap", minWidth: 0,
          }}>
            {ratingValue > 0 ? (
              // White chip with a filled yellow star: the rating is the signal
              // read first, and on a linen card a solid yellow block competed
              // with the FixyMaster chip instead of leading. The star carries
              // the colour and the number carries the contrast.
              //
              // `surface-raised` rather than a literal white — it is the token
              // for the lightest surface and stays legible when the palette
              // inverts, which #fff with dark text does not. The outline is an
              // inset shadow rather than a border so the chip keeps the exact
              // height of the three beside it.
              //
              // The star is a child rather than Badge's `icon` prop because
              // that one inherits the chip's text colour, and this glyph has to
              // be yellow while the number stays dark. Inline `fill` beats
              // Lucide's own `fill="none"` attribute.
              <Badge
                tone="neutral"
                style={{
                  whiteSpace: "nowrap",
                  background: "var(--surface-raised)",
                  color: "var(--text-body)",
                  boxShadow: "inset 0 0 0 1px var(--border-default)",
                }}
              >
                <Icon name="star" size={11}
                  style={{ color: "var(--hive)", fill: "var(--hive)" }} />
                {ratingValue.toFixed(1)}
              </Badge>
            ) : (
              <Badge tone="trust" style={{ whiteSpace: "nowrap" }}>{newLabel}</Badge>
            )}
            {jobs > 0 && (
              <Badge tone="neutral" icon="wrench" style={{ whiteSpace: "nowrap" }}>
                {jobsText}
              </Badge>
            )}
            {verified && (
              <Badge tone="success" icon="shield-check" style={{ whiteSpace: "nowrap" }}>
                {verifiedLabel}
              </Badge>
            )}
          </div>

          {/* Second level: where they are, and how far. Still useful, no longer
              the loudest thing on the card. */}
          <p style={{
            display: "flex", alignItems: "center", gap: 4, marginTop: "var(--space-2)",
            flexWrap: "wrap", fontSize: "var(--text-xs)", color: "var(--text-muted)",
          }}>
            <Icon name="map-pin" size={11} />{city}{state ? `, ${state}` : ""}
            {distance && (
              <>
                <span aria-hidden style={{ opacity: 0.5 }}>·</span>
                <Icon name="navigation" size={11} />{distance}
              </>
            )}
          </p>
        </div>
      </div>
      {bio && (
        <p style={{
          marginTop: "var(--space-3)", fontSize: "var(--text-sm)",
          color: "var(--text-muted)", lineHeight: "var(--leading-normal)",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{bio}</p>
      )}
      <div style={{
        marginTop: "var(--space-4)", display: "flex", justifyContent: "flex-end",
      }}>
        <Button variant="secondary" size="sm" iconRight="external-link" onClick={onVisit}>{ctaLabel}</Button>
      </div>
    </Card>
  );
}
